import { useEffect } from "react";
import { toSafeMessage } from "@/lib/safeMessage";

const isBrowser = typeof window !== "undefined";
const currentOrigin = isBrowser ? window.location.origin.replace(/\/+$/, "") : "";
const currentHost = isBrowser ? String(window.location.hostname || "").toLowerCase() : "";
const envBackend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const productionBackendByHost =
  currentHost === "app.obihub.app" || currentHost === "admin.obihub.app"
    ? "https://api.obihub.app"
    : "";
const isProductionHost = Boolean(productionBackendByHost);

function isPrivateOrLocalHost(hostname) {
  const host = String(hostname || "").toLowerCase();
  if (!host) return false;
  if (host === "localhost" || host === "::1" || host === "[::1]") return true;
  if (host.startsWith("127.")) return true;
  if (host.startsWith("10.")) return true;
  if (host.startsWith("192.168.")) return true;
  if (host.startsWith("172.")) {
    const second = Number(host.split(".")[1] || -1);
    if (second >= 16 && second <= 31) return true;
  }
  return false;
}

function sanitizeEnvBackend(url) {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    if (isProductionHost) {
      if (parsed.protocol !== "https:") return "";
      if (isPrivateOrLocalHost(parsed.hostname)) return "";
    }
    return parsed.toString().replace(/\/+$/, "");
  } catch {
    return "";
  }
}

const BACKEND_URL = (sanitizeEnvBackend(envBackend) || productionBackendByHost || currentOrigin || "http://localhost:4000").replace(/\/+$/, "");
const API_BASE = `${BACKEND_URL}/api`;

let lastKey = "";
let lastAt = 0;

function canSend(key) {
  const now = Date.now();
  if (key === lastKey && now - lastAt < 3000) return false;
  lastKey = key;
  lastAt = now;
  return true;
}

function isIgnorableScriptError(event) {
  const rawMessage = String(event?.message || "").trim().toLowerCase();
  const filename = String(event?.filename || "").trim().toLowerCase();
  const isScriptError = rawMessage === "script error.";
  if (!isScriptError) return false;

  // Browser extensions / cross-origin opaque script failures produce noisy, unusable logs.
  if (!filename) return true;
  if (
    filename.startsWith("chrome-extension://") ||
    filename.startsWith("moz-extension://") ||
    filename.startsWith("safari-extension://")
  ) {
    return true;
  }
  return false;
}

async function postClientError(payload) {
  const token = localStorage.getItem("obihub_token");
  if (!token) return;

  const key = `${payload?.message || ""}|${payload?.page || ""}`;
  if (!canSend(key)) return;

  try {
    await fetch(`${API_BASE}/client-errors`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
  } catch {
    // intentionally swallow logging failures
  }
}

export function ClientErrorMonitor() {
  useEffect(() => {
    const onError = (event) => {
      if (isIgnorableScriptError(event)) return;
      const message = toSafeMessage(event?.message || event?.error || "Unhandled error");
      const stack = toSafeMessage(event?.error?.stack || "");
      const page = window.location?.pathname || "";
      const isScriptError = String(event?.message || "").trim().toLowerCase() === "script error.";
      const filename = toSafeMessage(event?.filename || "");
      postClientError({
        source: "web",
        severity: "error",
        message: isScriptError && filename ? `${message} (${filename})` : message,
        stack,
        page,
        context: {
          userAgent: navigator.userAgent,
          type: "window.error",
          script_error: isScriptError,
          filename,
          lineno: Number(event?.lineno || 0) || null,
          colno: Number(event?.colno || 0) || null,
          href: toSafeMessage(window.location?.href || ""),
        },
      });
    };

    const onUnhandledRejection = (event) => {
      const reason = event?.reason;
      const message = toSafeMessage(
        typeof reason === "string" ? reason : reason?.message || reason || "Unhandled promise rejection"
      );
      const stack = toSafeMessage(reason?.stack || "");

      // Prevent noisy dev-overlay crashes for handled API rejection payloads.
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }

      const page = window.location?.pathname || "";
      postClientError({
        source: "web",
        severity: "error",
        message,
        stack,
        page,
        context: {
          userAgent: navigator.userAgent,
          type: "unhandledrejection",
        },
      });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  return null;
}
